import { economyFetch as fetch, economyAccessToken } from './economyClient';
/**
 * 投資システム サーバー連携
 *
 * Edge Function（investment）と連携し、
 * 投資データの取得・取引・借金・日経過処理を行う
 *
 * 独立関数として提供し、既存の投資システムから呼び出し可能
 */

// ========================================
// Supabase設定
// ========================================

const SUPABASE_URL = 'https://vikqkqwklbdjfhkydzpw.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZpa3FrcXdrbGJkamZoa3lkenB3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwNjYzODcsImV4cCI6MjA4MTY0MjM4N30.PPIf98mu1HgR95CaymSJiV9E5hnlsMJuQeXw2MNqKsw';
const AUTH_STORAGE_KEY = 'sb-vikqkqwklbdjfhkydzpw-auth-token';
const API_TIMEOUT_MS = 30000;
const getAccessToken = economyAccessToken;

function withTimeout<T>(promise: Promise<T>, ms: number, operation: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error(`${operation}: タイムアウト（${ms}ms）`));
    }, ms);

    promise
      .then((result) => {
        clearTimeout(timeout);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeout);
        reject(error);
      });
  });
}

// ========================================
// 型定義
// ========================================

export interface FundHoldingWithPnl {
  id: string;
  fundId: string;
  units: number;
  purchasePrice: number;
  purchaseDate: number;
  expiryDate: number | null;
  leverage: number;
  direction: 'long' | 'short';
  stopLoss: number | null;
  takeProfit: number | null;
  current_price: number;
  pnl: number;
  pnl_percent: number;
}

export interface FxPositionWithPnl {
  id: string;
  pair: string;
  direction: 'long' | 'short';
  leverage: number;
  margin: number;
  entryRate: number;
  openedAt: number;
  stopLoss: number | null;
  takeProfit: number | null;
  current_rate: number;
  pnl: number;
  pnl_percent: number;
}

/**
 * サーバーから返される借金データ（タイムスタンプベース）
 */
export interface DebtWithTimeInfo {
  amount: number;
  originalAmount: number;
  interest: number;
  deadline: number;           // UNIXタイムスタンプ（ミリ秒）
  createdAt: number;          // UNIXタイムスタンプ（ミリ秒）
  lastInterestAt: number;     // 最後に利息が加算された日時
  source: string;
  // サーバーで計算された残り時間情報
  remaining_ms: number;       // 残り時間（ミリ秒）
  remaining_days: number;     // 残り日数（小数）
  remaining_hours: number;    // 残り時間（小数）
  is_overdue: boolean;        // 期限切れか
  display_text: string;       // 表示用テキスト（「あと3日」「あと5時間」等）
}

// 後方互換性のためのエイリアス
export type DebtWithDays = DebtWithTimeInfo;

export interface InvestmentTotalStats {
  totalInvestments: number;
  totalProfitableTrades: number;
  totalInvestedAmount: number;
  totalPayout: number;
  totalProfit: number;
  totalFeesPaid: number;
}

// ========================================
// レスポンス型
// ========================================

export interface SyncResponse {
  success: boolean;
  error?: string;
  holdings?: FundHoldingWithPnl[];
  fx_positions?: FxPositionWithPnl[];
  debt?: DebtWithTimeInfo | null;
  total_stats?: InvestmentTotalStats;
  last_synced_at?: string;
}

export interface TradePosition {
  id: string;
  type: 'fund' | 'fx';
  entry_price?: number;
  entry_rate?: number;
  units?: number;
  margin?: number;
  leverage: number;
  direction: 'long' | 'short';
  opened_at: number;
  expiry_date?: number;
}

export interface TradeSettlement {
  position_id: string;
  exit_price?: number;
  exit_rate?: number;
  pnl: number;
  fee: number;
}

export interface TradeResponse {
  success: boolean;
  error?: string;
  position?: TradePosition;
  settlement?: TradeSettlement;
  new_gold_balance?: number;
}

/**
 * 借金操作のレスポンス
 * 借金作成時は新しいDebt情報を返す
 * 返済時はrepayment情報と残債を返す
 */
export interface DebtResponse {
  success: boolean;
  error?: string;
  debt?: {
    amount: number;
    originalAmount: number;
    interest: number;
    deadline: number;           // UNIXタイムスタンプ（ミリ秒）
    createdAt: number;          // UNIXタイムスタンプ（ミリ秒）
    lastInterestAt: number;     // 最後に利息が加算された日時
    source: string;
  } | null;
  repayment?: {
    amount_paid: number;
    remaining_debt: number;
  };
  new_gold_balance?: number;
}

export interface ProcessDayResponse {
  success: boolean;
  error?: string;
  triggered_orders?: {
    position_id: string;
    type: 'stop_loss' | 'take_profit';
    exit_price: number;
    pnl: number;
  }[];
  expired_funds?: {
    position_id: string;
    fund_id: string;
    exit_price: number;
    pnl: number;
  }[];
  interest_accrued?: {
    amount: number;
    new_total_debt: number;
  };
  debt_warning?: {
    remaining_days: number;
    amount: number;
  };
  seizure?: {
    triggered: boolean;
    seized_cards: string[];
    remaining_debt: number;
  };
  new_gold_balance?: number;
}

// ========================================
// API関数
// ========================================

/**
 * 投資データをサーバーから同期
 */
export async function syncInvestmentFromServer(): Promise<SyncResponse> {
  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      console.log('[syncInvestmentFromServer] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/investment/sync`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }),
      API_TIMEOUT_MS,
      'syncInvestmentFromServer'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[syncInvestmentFromServer] Investment data synced:', data);

    return data;
  } catch (err) {
    console.error('[syncInvestmentFromServer] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to sync investment data',
    };
  }
}

/**
 * 投資取引をサーバーに保存
 */
export async function saveInvestmentTradeToServer(params: {
  action: 'buy_fund' | 'sell_fund' | 'open_fx' | 'close_fx';
  fund_id?: string;
  pair?: string;
  units?: number;
  sell_units?: number;  // FIFO方式での売却口数
  margin?: number;
  leverage?: number;
  direction?: 'long' | 'short';
  stop_loss?: number;
  take_profit?: number;
  position_id?: string;
}): Promise<TradeResponse> {
  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      console.log('[saveInvestmentTradeToServer] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/investment/trade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(params),
      }),
      API_TIMEOUT_MS,
      'saveInvestmentTradeToServer'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[saveInvestmentTradeToServer] Trade saved:', data);

    return data;
  } catch (err) {
    console.error('[saveInvestmentTradeToServer] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to save trade',
    };
  }
}

/**
 * 借金操作をサーバーに保存
 */
export async function saveInvestmentDebtToServer(params: {
  action: 'borrow' | 'repay';
  amount: number;
}): Promise<DebtResponse> {
  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      console.log('[saveInvestmentDebtToServer] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/investment/debt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(params),
      }),
      API_TIMEOUT_MS,
      'saveInvestmentDebtToServer'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[saveInvestmentDebtToServer] Debt operation saved:', data);

    return data;
  } catch (err) {
    console.error('[saveInvestmentDebtToServer] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to save debt operation',
    };
  }
}

/**
 * 日経過処理をサーバーで実行
 */
export async function processInvestmentDayOnServer(
  currentBattleNumber: number
): Promise<ProcessDayResponse> {
  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      console.log('[processInvestmentDayOnServer] Not authenticated, skipping');
      return { success: false, error: 'Not authenticated' };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/investment/process-day`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ current_battle_number: currentBattleNumber }),
      }),
      API_TIMEOUT_MS,
      'processInvestmentDayOnServer'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[processInvestmentDayOnServer] Day processed:', data);

    return data;
  } catch (err) {
    console.error('[processInvestmentDayOnServer] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to process day',
    };
  }
}

// ========================================
// ヘルパー関数
// ========================================

/**
 * 認証済みかチェック（同期版）
 * トークンの有効期限はチェックしない（存在のみ確認）
 */
export function isInvestmentServerAvailable(): boolean {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return !!parsed?.access_token;
    }
  } catch {
    // エラー時はfalse
  }
  return false;
}

// ========================================
// エクスポート
// ========================================

export default {
  syncInvestmentFromServer,
  saveInvestmentTradeToServer,
  saveInvestmentDebtToServer,
  processInvestmentDayOnServer,
  isInvestmentServerAvailable,
};
